
import React, { useState, useMemo, useEffect, useCallback, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Clock, CheckCircle, AlertCircle, List, Plus, Printer,
  Table as TableIcon,
  Maximize2,
  Minimize2,
  Wifi,
  WifiOff,
  MapPin,
} from 'lucide-react';
import { Order } from '@/types';
import { ordersApi } from '@/services/api';
import { OrderViewMode } from '../OrdersPage';
import { cn } from '@/lib/utils';
import { DateFilter, DateFilterValue, DateRange } from './DateFilter';
import { OrdersTableView } from './OrdersTableView';
import { OrdersRoutesView } from './OrdersRoutesView';
import { WorkOrderDto } from '@/services/api';

// Lazy-load de diálogos pesados: solo entran al bundle cuando el usuario los abre
const OrderFormDialog = React.lazy(() =>
  import('./OrderFormDialog').then(m => ({ default: m.OrderFormDialog }))
);
const OrderDetailsDialog = React.lazy(() =>
  import('./OrderDetailsDialog').then(m => ({ default: m.OrderDetailsDialog }))
);
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";

interface OrdersListProps {
  onCreateNew?: () => void;
  onEditOrder?: (orderId: string) => void;
  viewMode: OrderViewMode;
  setViewMode: (mode: OrderViewMode) => void;
  registerOrderCreatedHandler: (handler: (order: Order) => void) => void;
  registerOrderUpdatedHandler: (handler: (order: Order) => void) => void;
  registerOrderDeletedHandler: (handler: (data: { id: string }) => void) => void;
  registerPrintAllHandler?: (handler: () => Promise<void>) => void;
  connectionStatus?: 'connected' | 'connecting' | 'disconnected';
}

// Helper functions for date filtering
const getDateRange = (filter: DateFilterValue, customRange?: DateRange): DateRange | null => {
  const today = new Date();
  
  // Función helper para formatear fecha en formato YYYY-MM-DD sin conversión UTC
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  switch (filter) {
    case 'today':
      return {
        from: formatDate(today),
        to: formatDate(today),
      };
    case 'tomorrow':
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return {
        from: formatDate(tomorrow),
        to: formatDate(tomorrow),
      };
    case 'day-after-tomorrow':
      const dayAfterTomorrow = new Date(today);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
      return {
        from: formatDate(dayAfterTomorrow),
        to: formatDate(dayAfterTomorrow),
      };
    case 'day-before-yesterday':
      const dayBeforeYesterday = new Date(today);
      dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
      return {
        from: formatDate(dayBeforeYesterday),
        to: formatDate(dayBeforeYesterday),
      };
    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: formatDate(yesterday),
        to: formatDate(yesterday),
      };
    case 'this-week':
      const firstDay = new Date(today);
      firstDay.setDate(today.getDate() - today.getDay() + 1); // Monday
      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6); // Sunday
      return {
        from: formatDate(firstDay),
        to: formatDate(lastDay),
      };
    case 'this-month':
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`,
        to: formatDate(lastOfMonth),
      };
    case 'custom':
      return customRange || null;
    default:
      return null;
  }
};

const isDateInRange = (dateStr: string, range: DateRange): boolean => {
  const date = new Date(dateStr);
  const from = new Date(range.from);
  const to = new Date(range.to);
  
  // Set all to midnight for comparison
  date.setHours(0, 0, 0, 0);
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  
  return date >= from && date <= to;
};

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  processing: { label: 'En Proceso', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle }
};

export const OrdersList: React.FC<OrdersListProps> = ({ 
  onCreateNew, 
  onEditOrder, 
  viewMode, 
  setViewMode,
  registerOrderCreatedHandler,
  registerOrderUpdatedHandler,
  registerOrderDeletedHandler,
  registerPrintAllHandler,
  connectionStatus
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'processing' | 'completed'>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('today');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Details Modal State
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState<Order | null>(null);

  // Form Modal State (New/Edit)
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedOrderIdForForm, setSelectedOrderIdForForm] = useState<string | undefined>();

  const handleCreateNew = useCallback(() => {
    setSelectedOrderIdForForm(undefined);
    setFormDialogOpen(true);
  }, []);

  const handleEditOrder = useCallback((id: string) => {
    setSelectedOrderIdForForm(id);
    setFormDialogOpen(true);
  }, []);

  // Permission checks
  const canEdit = usePermission(Permissions.WorkOrders.Edit);
  const canDelete = usePermission(Permissions.WorkOrders.Delete);

  // Helper para obtener fecha de hoy en formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Resuelve el rango (from, to) actualmente activo en el filtro de fecha.
  // Para el botón de imprimir lote: si el filtro no tiene rango (e.g. "all"),
  // cae a "hoy" como antes.
  const getActivePrintRange = useCallback((): { from: string; to: string; label: string } => {
    const range = getDateRange(dateFilter, customDateRange);
    if (range) {
      return {
        from: range.from,
        to: range.to,
        label: range.from === range.to ? range.from : `${range.from} → ${range.to}`,
      };
    }
    const today = getTodayDate();
    return { from: today, to: today, label: today };
  }, [dateFilter, customDateRange]);

  // Registrar handler para recibir nuevas órdenes de SignalR
  useEffect(() => {
    const handleNewOrder = (newOrder: Order) => {
      console.log('📥 Recibida nueva comanda desde SignalR:', newOrder);
      
      // Verificar si la orden cumple con los filtros actuales
      const matchesStatusFilter = activeTab === 'all' || newOrder.status === activeTab;
      
      let matchesDateFilter = true;
      const dateRange = getDateRange(dateFilter, customDateRange);
      if (dateRange) {
        matchesDateFilter = isDateInRange(newOrder.orderDate, dateRange);
      }
      
      // Solo agregar si cumple los filtros
      if (matchesStatusFilter && matchesDateFilter) {
        setOrders(prevOrders => {
          // Evitar duplicados
          const exists = prevOrders.some(o => o.id === newOrder.id);
          if (exists) return prevOrders;
          return [newOrder, ...prevOrders];
        });
      }
    };
    
    registerOrderCreatedHandler(handleNewOrder);
  }, [activeTab, dateFilter, customDateRange, registerOrderCreatedHandler]);

  // Registrar handler para actualizar órdenes desde SignalR
  useEffect(() => {
    const handleUpdatedOrder = (updatedOrder: Order) => {
      console.log('📝 Comanda actualizada desde SignalR:', updatedOrder);
      
      setOrders(prevOrders => {
        const exists = prevOrders.some(o => o.id === updatedOrder.id);
        
        if (exists) {
          // Verify if it still matches the filter (e.g. date changed)
          const dateRange = getDateRange(dateFilter, customDateRange);
          if (dateRange && !isDateInRange(updatedOrder.orderDate, dateRange)) {
             // It moved out of range, remove it
             return prevOrders.filter(o => o.id !== updatedOrder.id);
          }

          // Actualizar la orden existente
          return prevOrders.map(o => o.id === updatedOrder.id ? updatedOrder : o);
        } else {
          // Si no existe, verificar si cumple con los filtros y agregarla
          const matchesStatusFilter = activeTab === 'all' || updatedOrder.status === activeTab;
          
          let matchesDateFilter = true;
          const dateRange = getDateRange(dateFilter, customDateRange);
          if (dateRange) {
            matchesDateFilter = isDateInRange(updatedOrder.orderDate, dateRange);
          }
          
          if (matchesStatusFilter && matchesDateFilter) {
            return [updatedOrder, ...prevOrders];
          }
        }
        
        return prevOrders;
      });
    };
    
    registerOrderUpdatedHandler(handleUpdatedOrder);
  }, [activeTab, dateFilter, customDateRange, registerOrderUpdatedHandler]);

  // Registrar handler para eliminar órdenes desde SignalR
  useEffect(() => {
    const handleDeletedOrder = (data: { id: string }) => {
      console.log('🗑️ Comanda eliminada desde SignalR:', data.id);
      
      setOrders(prevOrders => prevOrders.filter(o => o.id !== data.id));
    };
    
    registerOrderDeletedHandler(handleDeletedOrder);
  }, [registerOrderDeletedHandler]);

  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Preparar parámetros para la API
      const params: any = {};
      
      // Filtro por fecha (para todos los modos)
      const dateRange = getDateRange(dateFilter, customDateRange);
      if (dateRange) {
        params.dateFrom = dateRange.from;
        params.dateTo = dateRange.to;
      }
      
      const data = await ordersApi.getAll(params);
      setOrders(data as unknown as Order[]);
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Error al cargar las comandas');
    } finally {
      setIsLoading(false);
    }
  }, [dateFilter, customDateRange]);

  // Cargar comandas desde la API
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle delete
  const handleDelete = useCallback(async (orderId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta comanda? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await ordersApi.delete(orderId);
      
      // Eliminar localmente
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      alert('Comanda eliminada correctamente');
    } catch (err: any) {
      console.error('Error deleting order:', err);
      alert('Error al eliminar la comanda');
    }
  }, []);

  // Handle print
  const handlePrint = async (orderId: string) => {
    try {
      const res = await ordersApi.printOrder(orderId);
      alert(res.message || 'Solicitud de impresión enviada correctamente.');
    } catch (err: any) {
      console.error('Error al imprimir comanda:', err);
      alert(err.message || 'Error al imprimir la comanda');
    }
  };

  useEffect(() => {
    if (!registerPrintAllHandler) return;

    const printAll = async () => {
      try {
        const { from, to } = getActivePrintRange();
        const res = await ordersApi.printBatchByRange(from, to);
        alert(res.message || 'Solicitud de impresión enviada correctamente.');
      } catch (err: any) {
        console.error('Error al imprimir batch:', err);
        alert('Error al imprimir: ' + (err.message || err));
      }
    };

    registerPrintAllHandler(printAll);

    return () => {
      try {
        // limpiar registrador pasando un noop
        registerPrintAllHandler(() => Promise.resolve());
      } catch (_) {
        // ignore
      }
    };
  }, [orders, registerPrintAllHandler]);

  // Filter orders by date first
  const dateFilteredOrders = useMemo(() => {
    if (dateFilter === 'all') return orders;
    
    const range = getDateRange(dateFilter, customDateRange);
    if (range) {
      return orders.filter(order => isDateInRange(order.orderDate, range));
    }
    return orders;
  }, [orders, dateFilter, customDateRange]);

  // Count orders by status (respecting date filter)
  const orderCounts = useMemo(() => ({
    all: dateFilteredOrders.length,
    pending: dateFilteredOrders.filter(o => o.status === 'pending').length,
    processing: dateFilteredOrders.filter(o => o.status === 'processing').length,
    completed: dateFilteredOrders.filter(o => o.status === 'completed').length,
  }), [dateFilteredOrders]);

  // Filter orders based on active tab and date
  const filteredOrders = useMemo(() => {
    // Start with date-filtered orders
    const filteredList = activeTab === 'all'
      ? dateFilteredOrders
      : dateFilteredOrders.filter(order => order.status === activeTab);

    // Sort by createdAt descending (newest first). Cache parsed timestamps
    // so each order's createdAt is only converted once per recompute.
    const withTs = filteredList.map(o => ({ o, ts: new Date(o.createdAt).getTime() }));
    withTs.sort((a, b) => b.ts - a.ts);
    return withTs.map(x => x.o);
  }, [activeTab, dateFilteredOrders]);

  const handleOpenDetails = useCallback((order: Order) => {
    setSelectedOrderForDetails(order);
    setDetailsDialogOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsDialogOpen(false);
    setSelectedOrderForDetails(null);
  }, []);

  const handlePrintOrder = useCallback(async (orderId: string) => {
    try {
      const res = await ordersApi.printOrder(orderId);
      alert(res.message || 'Comanda enviada a imprimir correctamente');
    } catch (err: any) {
      console.error('Error al imprimir comanda:', err);
      alert('Error al imprimir: ' + (err.message || err));
    }
  }, []);

  // Memoizar el rango de impresión activo para no recalcular en cada render.
  const activePrintRange = useMemo(() => getActivePrintRange(), [getActivePrintRange]);

  const cardContent = (
    <Card className={cn(
      "shadow-sm border-slate-200 transition-all duration-300",
      isFullscreen && "fixed inset-0 z-50 rounded-none m-0 h-screen w-screen overflow-auto bg-white"
    )}>
      <CardHeader className="flex flex-row flex-wrap items-center gap-4 space-y-0">
          {/* First Row: Connection Status + Date Filter */}
          <div className="flex items-center gap-4 w-full xl:w-auto min-h-[32px]">
             {connectionStatus && (
                 <Tooltip>
                   <TooltipTrigger asChild>
                      <div className={`flex items-center justify-center p-2 rounded-md border ${
                        connectionStatus === 'connected' ? 'bg-green-50 border-green-200 text-green-700' : 
                        connectionStatus === 'connecting' ? 'bg-slate-50 border-slate-200 text-slate-600' : 
                        'bg-red-50 border-red-200 text-red-700'
                      }`}>
                        {connectionStatus === 'connected' ? (
                            <Wifi className="h-4 w-4" />
                        ) : connectionStatus === 'connecting' ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-300 border-t-slate-600"></div>
                        ) : (
                            <WifiOff className="h-4 w-4" />
                        )}
                      </div>
                   </TooltipTrigger>
                   <TooltipContent>
                      <p>{connectionStatus === 'connected' ? 'Conectado' : connectionStatus === 'connecting' ? 'Conectando...' : 'Sin conexión'}</p>
                   </TooltipContent>
                 </Tooltip>
             )}
             
             <DateFilter
              value={dateFilter}
              customRange={customDateRange}
              onChange={(value, range) => {
                setDateFilter(value);
                setCustomDateRange(range);
              }}
            />
          </div>
          
          {/* Second Row: View Mode Toggle & Actions */}
          <div className="flex items-center gap-3 w-full xl:w-auto justify-end xl:ml-auto">
            {/* Total Orders Counter */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
              <span className="text-sm font-medium text-slate-600">Total:</span>
              <span className="text-sm font-bold text-slate-900">{filteredOrders.length} pedidos</span>
            </div>

            {/* Fullscreen Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="hidden md:inline-flex"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}</p>
              </TooltipContent>
            </Tooltip>

            {/* View Mode Toggle */}
            <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v as OrderViewMode)} className="bg-white border rounded-lg p-0.5 shadow-sm">
                {/* General (operator) oculto */}

                {/* Tablero (cards) oculto — usar vista lista por defecto */}

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem 
                      value="table" 
                      className={cn(
                        "transition-colors",
                        viewMode === 'table' 
                          ? "bg-green-100 text-green-700 font-medium" 
                          : "text-slate-500 hover:text-slate-700"
                      )}
                    >
                      <TableIcon size={16} />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Lista</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem 
                      value="routes" 
                      className={cn(
                        "transition-colors",
                        viewMode === 'routes' 
                          ? "bg-green-100 text-green-700 font-medium" 
                          : "text-slate-500 hover:text-slate-700"
                      )}
                    >
                      <MapPin size={16} />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Rutas</p>
                  </TooltipContent>
                </Tooltip>
              </ToggleGroup>

            {/* Batch Print Button (Common) */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                  onClick={async () => {
                    try {
                      const { from, to } = activePrintRange;
                      const res = await ordersApi.printBatchByRange(from, to);
                      alert(res.message || 'Solicitud de impresión enviada correctamente.');
                    } catch (err: any) {
                      console.error('Error al imprimir batch:', err);
                      alert('Error al imprimir: ' + (err.message || err));
                    }
                  }}
                >
                  <Printer size={18} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Imprimir lote ({activePrintRange.label})</p>
              </TooltipContent>
            </Tooltip>
            
            {/* New Order Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  size="icon"
                  onClick={handleCreateNew}
                >
                  <Plus size={18} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Nueva Venta</p>
              </TooltipContent>
            </Tooltip>
          </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
              <p className="text-slate-600">Cargando comandas...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
              <p className="text-red-600 font-medium mb-2">Error al cargar las comandas</p>
              <p className="text-slate-600 text-sm">{error}</p>
            </div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="bg-slate-50 p-4 rounded-full inline-block mb-3">
                <List className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-lg font-medium text-slate-900">No hay comandas</p>
              <p className="text-slate-500">No se encontraron comandas con los filtros actuales</p>
            </div>
          </div>
        ) : (
          <>
            {viewMode === 'table' && (
              <OrdersTableView
                orders={filteredOrders}
                onEdit={handleEditOrder}
                onDelete={handleDelete}
                onViewDetails={handleOpenDetails}
                onPrint={handlePrintOrder}
                statusConfig={statusConfig}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            )}

            {viewMode === 'routes' && (
              <OrdersRoutesView
                orders={filteredOrders}
                onEdit={handleEditOrder}
                onDelete={handleDelete}
                onViewDetails={handleOpenDetails}
                onPrint={handlePrintOrder}
                statusConfig={statusConfig}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full">
      {detailsDialogOpen && (
        <Suspense fallback={null}>
          <OrderDetailsDialog
            isOpen={detailsDialogOpen}
            onClose={handleCloseDetails}
            order={selectedOrderForDetails}
          />
        </Suspense>
      )}

      {formDialogOpen && (
        <Suspense fallback={null}>
          <OrderFormDialog
            isOpen={formDialogOpen}
            onClose={() => {
              setFormDialogOpen(false);
              setSelectedOrderIdForForm(undefined);
            }}
            orderId={selectedOrderIdForForm}
            onSuccess={fetchOrders}
          />
        </Suspense>
      )}
      
      {/* Status Filters (Cards) - COMMENTED OUT - Use Cards view to see status breakdown */}
      {/* {!isFullscreen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <button
          onClick={() => setActiveTab('all')}
          className={`text-left bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3 md:p-5 border-2 transition-all hover:shadow-md ${
            activeTab === 'all' ? 'border-slate-400 shadow-md' : 'border-slate-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-slate-800">Todas</p>
              <p className="text-xl md:text-3xl font-bold text-slate-900 mt-1">{orderCounts.all}</p>
            </div>
            <List className="text-slate-600 w-6 h-6 md:w-8 md:h-8" />
          </div>
        </button>

        <button
          onClick={() => setActiveTab('pending')}
          className={`text-left bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-3 md:p-5 border-2 transition-all hover:shadow-md ${
            activeTab === 'pending' ? 'border-yellow-400 shadow-md' : 'border-yellow-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-yellow-800">Pendientes</p>
              <p className="text-xl md:text-3xl font-bold text-yellow-900 mt-1">{orderCounts.pending}</p>
            </div>
            <Clock className="text-yellow-600 w-6 h-6 md:w-8 md:h-8" />
          </div>
        </button>

        <button
          onClick={() => setActiveTab('processing')}
          className={`text-left bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 md:p-5 border-2 transition-all hover:shadow-md ${
            activeTab === 'processing' ? 'border-blue-400 shadow-md' : 'border-blue-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-blue-800">En Proceso</p>
              <p className="text-xl md:text-3xl font-bold text-blue-900 mt-1">{orderCounts.processing}</p>
            </div>
            <AlertCircle className="text-blue-600 w-6 h-6 md:w-8 md:h-8" />
          </div>
        </button>

        <button
          onClick={() => setActiveTab('completed')}
          className={`text-left bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 md:p-5 border-2 transition-all hover:shadow-md ${
            activeTab === 'completed' ? 'border-green-400 shadow-md' : 'border-green-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-green-800">Completadas</p>
              <p className="text-xl md:text-3xl font-bold text-green-900 mt-1">{orderCounts.completed}</p>
            </div>
            <CheckCircle className="text-green-600 w-6 h-6 md:w-8 md:h-8" />
          </div>
        </button>
      </div>
      )} */}

      {/* Main Content Card */}
      {isFullscreen ? (
        typeof document !== 'undefined' && createPortal(cardContent, document.body)
      ) : (
        cardContent
      )}
    </div>
  );
};
